import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { projectsAPI } from "../../api/client";
import useAuth from "../../hooks/useAuth";
import {
  PROJECT_STATUS_OPTIONS,
  formatProjectStatus,
} from "../../utils/formatProjectStatus";

const MAX_REASON_LENGTH = 1000;
const POPOVER_WIDTH = 320;
const GAP = 8;

// Admin-only: move a project to any status outside its workflow. The reason is
// mandatory and is printed on the project report. Opens from an icon button
// and anchors under it; rendered into <body> so the card can't clip it.
export function ForceStatusButton({ project, onForced }) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef(null);

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        className={`force-status-trigger ${open ? "open" : ""}`.trim()}
        onClick={() => setOpen((v) => !v)}
        aria-label="Force status"
        aria-haspopup="dialog"
        aria-expanded={open}
        title="Force status (admin, beta)"
      >
        <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true">
          <path
            d="M7 7h11l-3-3M17 17H6l3 3"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open && (
        <ForceStatusPopover
          project={project}
          anchorRef={anchorRef}
          onClose={() => setOpen(false)}
          onForced={onForced}
        />
      )}
    </>
  );
}

function ForceStatusPopover({ project, anchorRef, onClose, onForced }) {
  const { invoicingEnabled } = useAuth();
  const options = PROJECT_STATUS_OPTIONS.filter(
    (o) =>
      o.value !== project.status &&
      (invoicingEnabled || o.value !== "AWAITING_INVOICE"),
  );
  const [status, setStatus] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [position, setPosition] = useState(null);
  const popoverRef = useRef(null);

  // Below the icon, right edges aligned; flips above when there's no room and
  // stays inside the viewport horizontally.
  useLayoutEffect(() => {
    const place = () => {
      const anchor = anchorRef.current?.getBoundingClientRect();
      if (!anchor) return;
      const height = popoverRef.current?.offsetHeight || 0;
      const width = Math.min(POPOVER_WIDTH, window.innerWidth - GAP * 2);
      let left = anchor.right - width;
      left = Math.max(GAP, Math.min(left, window.innerWidth - width - GAP));
      let top = anchor.bottom + GAP;
      if (top + height > window.innerHeight - GAP && anchor.top - GAP - height > GAP) {
        top = anchor.top - GAP - height;
      }
      setPosition({ top, left, width });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [anchorRef, status, error]);

  useEffect(() => {
    const onDown = (e) => {
      if (popoverRef.current?.contains(e.target)) return;
      if (anchorRef.current?.contains(e.target)) return;
      onClose();
    };
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [anchorRef, onClose]);

  const closing = status === "COMPLETED" || status === "AWAITING_INVOICE";
  const canSubmit = Boolean(status && reason.trim()) && !saving;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    try {
      setSaving(true);
      setError("");
      await projectsAPI.forceStatus(project.id, status, reason.trim());
      onClose();
      onForced?.();
    } catch (err) {
      setError(err.response?.data?.message || "Failed to force status");
      setSaving(false);
    }
  };

  return createPortal(
    <form
      ref={popoverRef}
      className="force-status-popover"
      role="dialog"
      aria-label={`Force status for ${project.name}`}
      onSubmit={submit}
      style={
        position
          ? { top: position.top, left: position.left, width: position.width }
          : { visibility: "hidden" }
      }
    >
      <div className="fsp-head">
        <span className="fsp-title">
          Force status <span className="fsp-beta">Beta</span>
        </span>
        <span className="fsp-current">
          Now: <span className={`status-badge status-${project.status}`}>
            {formatProjectStatus(project.status)}
          </span>
        </span>
      </div>

      <div className="fsp-label">Move to</div>
      <div className="fsp-options" role="radiogroup" aria-label="New status">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={status === o.value}
            className={`fsp-option ${status === o.value ? "selected" : ""}`.trim()}
            onClick={() => setStatus(o.value)}
          >
            {o.label}
          </button>
        ))}
      </div>

      <label className="fsp-label" htmlFor={`fsp-reason-${project.id}`}>
        Reason <span className="fsp-required">*</span>
      </label>
      <textarea
        id={`fsp-reason-${project.id}`}
        className="fsp-reason"
        rows={3}
        maxLength={MAX_REASON_LENGTH}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        placeholder="Shown on the project report"
      />

      {closing && (
        <p className="fsp-note">
          Open stages will be closed and removed from people's task lists.
        </p>
      )}
      {error && <p className="fsp-error">{error}</p>}

      <div className="fsp-foot">
        <button type="button" className="btn-secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {saving ? "Saving…" : "Apply"}
        </button>
      </div>
    </form>,
    document.body,
  );
}
